import { ServiceUnreachableError } from '@fleece/utilities';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Moon, Settings, Sun } from 'lucide-react';
import { useState } from 'react';
import { ConnectionControl } from '@/components/connection-control';
import { PreferencesDialog } from '@/components/preferences-dialog';
import { SetupScreen } from '@/components/setup-screen';
import { Button, Spinner } from '@/components/ui';
import { ConnectionProvider, useConnection } from '@/hooks/use-connection';
import { PreferencesProvider } from '@/hooks/use-preferences';
import { useTheme } from '@/hooks/use-theme';
import { easternDate } from '@/lib/format';
import { CreditSpreadsPage } from '@/pages/credit-spreads-page';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // Retry only what a retry can fix: the service being momentarily unreachable. A bad
        // ticker or a refused backtest answers the same way the second time.
        retry: (failures, error) => failures < 2 && error instanceof ServiceUnreachableError,
      },
      mutations: { retry: false },
    },
  });
}

/** Deliberately almost nothing: one round trip, usually to the same machine. */
function ConnectingSplash() {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-24 text-xs text-muted">
      <Spinner />
      Connecting…
    </div>
  );
}

/**
 * A gate rather than a branch inside each panel: everything on the page needs a client to
 * call, so rendering it without one means every panel discovering the same missing answer
 * separately.
 */
function ConnectedPage() {
  const { state } = useConnection();
  if (state.status === 'probing') {
    return <ConnectingSplash />;
  }
  if (state.status === 'setup') {
    return <SetupScreen initial={state.candidate} outcome={state.outcome} />;
  }
  // Keyed by service, so switching to another one starts the page afresh: a backtest id,
  // a symbol or a picked spread from the last service means nothing to the next, and
  // polling it with the old backtest's id is the request the switch is meant to prevent.
  return <CreditSpreadsPage key={state.connection.apiUrl} />;
}

function Shell() {
  const [theme, toggleTheme] = useTheme();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [today] = useState(() => easternDate(Date.now()));
  return (
    <>
      <div className="min-h-dvh">
        <header className="sticky top-0 z-20 border-b border-line bg-page/90 backdrop-blur">
          <div className="mx-auto flex h-10 max-w-[1400px] items-center gap-2 px-2 sm:px-3">
            <span className="text-sm font-bold tracking-tight">Fleece</span>
            <span className="text-xs text-muted">/</span>
            <span className="text-xs font-medium text-ink-2">Credit spreads</span>
            <span className="ml-auto" />
            <ConnectionControl />
            <Button variant="ghost" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
              {theme === 'dark' ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            </Button>
            <Button variant="ghost" onClick={() => setPreferencesOpen(true)} aria-label="Preferences" aria-haspopup="dialog">
              <Settings className="size-3.5" />
            </Button>
          </div>
        </header>
        <main>
          <ConnectedPage />
        </main>
      </div>
      <PreferencesDialog open={preferencesOpen} today={today} onClose={() => setPreferencesOpen(false)} />
    </>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      {/* Inside the query client, because switching service clears its cache. */}
      <ConnectionProvider>
        <PreferencesProvider>
          <Shell />
        </PreferencesProvider>
      </ConnectionProvider>
    </QueryClientProvider>
  );
}
